import React, { useMemo, useState } from 'react';
import { Container, Divider, ListItemIcon, Stack, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { hasOnboardingContent, ProgrammeOnboardingDialog } from 'src/components/dialogs/programme-onboarding';
import { useToggle } from 'src/hooks/useToggle';
import { MenuItem } from './components/item';
import { MenuColumn, MenuRow } from './components/column';
import { AppButton, AppTypography, StackColumn, StackRow } from 'src/components';
import { ContactUs } from './components/contact-us';
import { FeatureArticles } from './components/feature-article';
import { MenuLink } from './components/link';
import { SidebarItemProps } from '../sidebar/sidebar';
import TextMaxLine from 'src/components/TextMaxLine';
import { IconChevronRight, IconLock } from 'src/libraries/icons';
import { getFeaturesRequirementsAPI } from 'src/resources/api/features/index.api';
import { NoFeatureAccessBanner } from 'src/components/banners/no-feature-access-banner';
import { TopNavigationItems } from 'src/layouts/dashboard/navbar/NavConfig';
import useMerchant from 'src/hooks/useMerchant';
import useSubscriptions from 'src/hooks/useSubscriptions';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';

//
//

const PARENT_ITEM_HEIGHT = 74;

//

const StyledPaper = styled(Container)(({ theme }) => ({
  width: '100%',
  left: 0,
  right: 0,
  position: 'absolute',
  top: PARENT_ITEM_HEIGHT,
  zIndex: theme.zIndex.modal,
  filter: 'drop-shadow(1px 1px 6px rgba(0, 0, 0, 0.25))',
  minHeight: 250,
  display: 'flex',
  backgroundColor: theme.palette.background.paper,
}));

type MegaMenuProps = {
  megaMenuNavConfig: SidebarItemProps[];
};

export const MegaMenu = ({ megaMenuNavConfig, ...other }: MegaMenuProps) => {
  return (
    <Stack direction="row" spacing={0} color="text.primary" {...other}>
      {megaMenuNavConfig.map((parent) => (
        <MegaMenuSection key={parent.title} parent={parent} />
      ))}
    </Stack>
  );
};

//

const MegaMenuSection = ({ parent }: { parent: any }) => {
  const [open, setOpen] = useState(false);
  const onboardingToggle = useToggle();
  const navigate = useNavigate();

  const { merchant } = useMerchant();
  const { currentPlan } = useSubscriptions();

  const showsOnboarding = useMemo(() => {
    return hasOnboardingContent(currentPlan?.packageCode);
  }, [currentPlan?.packageCode]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const getFeaturesRequirementsHook = useQuery({
    queryKey: ['features-requirements'],
    queryFn: () => getFeaturesRequirementsAPI(),
  });

  const disabledMenuItems = parent?.children?.filter((child: any) => child?.disabled);

  const lowestDisabledFeature = useMemo(() => {
    return (
      (getFeaturesRequirementsHook?.data || [])
        .filter((f: { featureCode: string }) =>
          disabledMenuItems?.some(
            (item: { disabled?: boolean; featureCode?: string }) =>
              item.disabled && item.featureCode === f.featureCode
          )
        )
        .sort((a: any, b: any) => a.requiredPlan - b.requiredPlan)[0] || null
    );
  }, [disabledMenuItems, getFeaturesRequirementsHook?.data]);

  if (!parent) return null;

  const programmeItems =
    parent?.name === TopNavigationItems.programmes
      ? parent?.children?.filter((item: { code?: string }) => 'code' in item)
      : [];
  const nonProgrammeItems =
    parent?.name === TopNavigationItems.programmes
      ? parent?.children?.filter((item: { code?: string }) => !('code' in item))
      : [];

  if (parent?.children?.length) {
    return (
      <>
        <ProgrammeOnboardingDialog
          open={onboardingToggle.value}
          onClose={onboardingToggle.off}
          packageCode={currentPlan?.packageCode}
        />

        <MenuItem
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
          {...parent}
          open={open}
          hasSub
        />

        {open && (
          <StyledPaper maxWidth="xl" onMouseEnter={handleOpen} onMouseLeave={handleClose}>
            <MenuRow>
              {parent.name === TopNavigationItems.programmes ? (
                <>
                  <MenuColumn sx={{ justifyContent: 'space-between' }}>
                    <StackColumn>
                      <AppTypography variant="system" color="grey.300">
                        My programmes
                      </AppTypography>

                      {programmeItems?.slice(0, 4).map((item: SidebarItemProps) => (
                        <MegaMenuSectionItem
                          key={item.id || item.title}
                          icon={item.icon}
                          to={item.path}
                          description={item.subtitle}
                          defaultPath={item?.defaultPath}
                          {...item}
                        />
                      ))}

                      {merchant && !programmeItems?.length && (
                        <AppTypography>There are no active programmes.</AppTypography>
                      )}
                    </StackColumn>

                    <StackColumn>
                      <Divider />

                      <AppButton
                        sx={{
                          ml: 0,
                          mr: 'auto',
                          '&:hover': {
                            fontWeight: 700,
                            '& .MuiButton-endIcon': { transform: 'none' },
                          },
                        }}
                        onClick={() => navigate(PATH_DASHBOARD.programs.root)}
                        endIcon={<IconChevronRight size={18} />}
                      >
                        {merchant && programmeItems?.length
                          ? 'See all programmes'
                          : 'Visit Programmes page'}
                      </AppButton>
                    </StackColumn>
                  </MenuColumn>

                  <MenuColumn>
                    {nonProgrammeItems.map((item: SidebarItemProps) => (
                      <MegaMenuSectionItem
                        key={item.id || item.title}
                        icon={item.icon}
                        to={item.path}
                        description={item.subtitle}
                        defaultPath={item?.defaultPath}
                        {...item}
                      />
                    ))}
                  </MenuColumn>
                </>
              ) : (
                <MenuColumn>
                  {parent.children?.map((item: SidebarItemProps) => (
                    <MegaMenuSectionItem
                      key={item.id || item.title}
                      icon={item.icon}
                      to={item.path}
                      defaultPath={item?.defaultPath}
                      description={item.subtitle}
                      {...item}
                      // disabled={parent.name === 'projects' ? false : item?.disabled}
                    />
                  ))}
                </MenuColumn>
              )}

              {/* Note: if menu item is programmes and the current package has onboarding content -> display Onboarding panel */}
              {parent?.name === TopNavigationItems.programmes && showsOnboarding ? (
                <ContactUs name={parent?.name} onOpenOnboarding={onboardingToggle.on} />
              ) : lowestDisabledFeature ? (
                <MenuColumn showBackground sx={{ p: 0 }}>
                  <NoFeatureAccessBanner
                    isNavigation
                    menuSection={parent.name}
                    planTier={lowestDisabledFeature?.requiredPlan}
                    title={noAccessMenuBannerCopies?.find((copy) => copy?.code === parent?.name)?.title || ''}
                    description={noAccessMenuBannerCopies?.find((copy) => copy?.code === parent?.name)?.subtitle || ''}
                  />
                </MenuColumn>
              ) : (
                <>
                  <ContactUs
                    name={
                      parent?.name === TopNavigationItems.programmes
                        ? '_programmes'
                        : parent?.name
                    }
                    onOpenOnboarding={onboardingToggle.on}
                  />

                  {parent?.name === TopNavigationItems.communication && (
                    <MenuColumn showBackground>
                      <AppTypography fontWeight="bold" variant="body2">
                        Featured articles
                      </AppTypography>

                      <FeatureArticles />
                    </MenuColumn>
                  )}
                </>
              )}
            </MenuRow>
          </StyledPaper>
        )}
      </>
    );
  }

  return null;
};

//

type MegaMenuSectionItemProps = {
  icon: any;
  defaultPath?: string;
  to: string;
  title: string;
  description?: string;
  disabled?: boolean;
};

export const MegaMenuSectionItem: React.FC<MegaMenuSectionItemProps> = ({
  icon,
  defaultPath,
  to = '#',
  title,
  description,
  disabled,
}) => {
  return (
    <StackRow alignItems="flex-start">
      {icon && (
        <ListItemIcon
          sx={{ mr: 0, width: 18, height: 18, '& svg': { width: '100%', height: '100%' } }}
        >
          {icon}
        </ListItemIcon>
      )}

      <StackColumn gap={0.5}>
        <MenuLink
          to={defaultPath || to}
          title={title}
          endIcon={disabled ? <IconLock sx={{ height: 16 }} /> : null}
        />

        {description && (
          <TextMaxLine line={2} variant="body2">
            {description}
          </TextMaxLine>
        )}
      </StackColumn>
    </StackRow>
  );
};

//

const noAccessMenuBannerCopies = [
  {
    code: TopNavigationItems.offsets,
    title: 'Subscribe to unlock footprints and offsetting',
    subtitle:
      "You can view your past offsets here, but you'll need a subscription to calculate new footprints and purchase offsets through the portal.",
  },
  {
    code: TopNavigationItems.programmes,
    title: 'Subscribe to provide carbon offsetting options to your customers',
    subtitle:
      'CarbonClick offers a range of tools to help your customers reduce or offset their emissions – from calculators and reduction tips to fully integrated offsetting experiences. We can help you design a programme that fits your business.',
  },
  {
    code: TopNavigationItems.communication,
    title: 'Subscribe to tell your sustainability story',
    subtitle:
      "Communications tools help you tell your climate story – from showcasing your impact to sharing the projects you support. Use ready-made assets, widgets, and shareable pages to engage your audience and show the impact you're making.",
  },
  {
    code: TopNavigationItems.reports,
    title: 'Subscribe to unlock full carbon reports',
    subtitle:
      'Explore your carbon impact through detailed reporting. Unlock insights across individual and business customers, track trends, and better understand your sustainability performance.',
  },
];
